/**
 * repo.sessions.ts —— 会话仓储
 *
 * 会话是「我」与某个联系人之间的对话容器。
 * 一个联系人对多会话：正常情况下一对一，但删了再建会产生新会话，
 * 所以用 contactId 查出来的是数组而不是单条。
 */

import { STORE, dbGet, dbGetAll, dbPut, dbDelete, dbQueryByIndex } from './db';
import { eventBus } from '@/core/event-bus';
import { uid } from './db';
import type { Session, Contact } from '@/types/models';

/** 会话 + 联系人信息的组合视图 —— 列表渲染需要展示对方名字 */
export interface SessionView extends Session {
  contact: Contact | undefined;
}

/** 列表排序：置顶优先，然后按最后消息时间倒序 */
function sortSessions(list: Session[]): Session[] {
  return list.sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return b.lastMessageAt - a.lastMessageAt;
  });
}

export const sessionsRepo = {
  /** 全部会话（已排序），并挂上对应的联系人 */
  async listWithContact(): Promise<SessionView[]> {
    const [sessions, contacts] = await Promise.all([
      dbGetAll<Session>(STORE.sessions),
      dbGetAll<Contact>(STORE.contacts),
    ]);
    const byId = new Map(contacts.map((c) => [c.id, c]));
    return sortSessions(sessions).map((s) => ({
      ...s,
      contact: byId.get(s.contactId),
    }));
  },

  async get(id: string): Promise<Session | undefined> {
    return dbGet<Session>(STORE.sessions, id);
  },

  /** 某联系人的全部会话，按时间倒序 */
  async listByContact(contactId: string): Promise<Session[]> {
    const all = await dbQueryByIndex<Session>(STORE.sessions, 'contactId', contactId);
    return all.sort((a, b) => b.lastMessageAt - a.lastMessageAt);
  },

  /** 取（或创建）与某联系人的会话。已存在时返回时间最近的那个 */
  async ensureForContact(contactId: string): Promise<Session> {
    const existing = await this.listByContact(contactId);
    if (existing.length > 0) return existing[0];

    const session: Session = {
      id: uid(),
      contactId,
      lastMessageAt: Date.now(),
      lastPreview: '',
      unread: 0,
      pinned: false,
    };
    await dbPut(STORE.sessions, session);
    eventBus.emit('session:list-changed', {});
    return session;
  },

  /** 更新会话的最后消息信息（发消息后调用） */
  async touch(sessionId: string, preview: string, at: number): Promise<void> {
    const s = await dbGet<Session>(STORE.sessions, sessionId);
    if (!s) return;
    s.lastMessageAt = at;
    s.lastPreview = preview;
    await dbPut(STORE.sessions, s);
    eventBus.emit('session:list-changed', {});
  },

  /** 未读数 +1 */
  async incrementUnread(sessionId: string): Promise<void> {
    const s = await dbGet<Session>(STORE.sessions, sessionId);
    if (!s) return;
    s.unread += 1;
    await dbPut(STORE.sessions, s);
    eventBus.emit('session:list-changed', {});
  },

  /** 标记已读 */
  async markRead(sessionId: string): Promise<void> {
    const s = await dbGet<Session>(STORE.sessions, sessionId);
    if (!s || s.unread === 0) return;
    s.unread = 0;
    await dbPut(STORE.sessions, s);
    eventBus.emit('session:list-changed', {});
  },

  /** 全部标记已读 —— 批量能力放在宿主侧，不让 AI 写循环（D3） */
  async markAllRead(): Promise<number> {
    const all = await dbGetAll<Session>(STORE.sessions);
    const dirty = all.filter((s) => s.unread > 0);
    if (dirty.length === 0) return 0;
    await Promise.all(dirty.map((s) => dbPut(STORE.sessions, { ...s, unread: 0 })));
    eventBus.emit('session:list-changed', {});
    return dirty.length;
  },

  async setPinned(sessionId: string, pinned: boolean): Promise<void> {
    const s = await dbGet<Session>(STORE.sessions, sessionId);
    if (!s) return;
    s.pinned = pinned;
    await dbPut(STORE.sessions, s);
    eventBus.emit('session:list-changed', {});
  },

  /** 删除会话及其全部消息、文件关联 */
  async remove(sessionId: string): Promise<void> {
    const [messages, files] = await Promise.all([
      dbQueryByIndex<{ id: string }>(STORE.messages, 'sessionId', sessionId),
      dbQueryByIndex<{ id: string }>(STORE.files, 'sessionId', sessionId),
    ]);

    for (const m of messages) await dbDelete(STORE.messages, m.id);
    for (const f of files) await dbDelete(STORE.files, f.id);
    await dbDelete(STORE.sessions, sessionId);

    eventBus.emit('session:list-changed', {});
    eventBus.emit('message:list-changed', { sessionId });
  },
};
